// @ts-nocheck
import React, { createContext, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type ModuleKey = string;

export interface OrgModule {
  module_key: string;
  label: string;
  description: string | null;
  category: string | null;
  sort_order: number | null;
  is_enabled: boolean;
}

interface OrgModulesValue {
  /** Fail-open: unknown keys, missing org, and loading all resolve to true. */
  isEnabled: (key: ModuleKey) => boolean;
  modules: OrgModule[];
  moduleMap: Record<string, boolean>;
  isLoading: boolean;
  orgId: string | null;
  setModule: (key: ModuleKey, enabled: boolean) => Promise<void>;
  refetch: () => void;
}

const defaultValue: OrgModulesValue = {
  isEnabled: () => true,
  modules: [],
  moduleMap: {},
  isLoading: false,
  orgId: null,
  setModule: async () => {},
  refetch: () => {},
};

const OrgModulesContext = createContext<OrgModulesValue>(defaultValue);

export const OrgModulesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const orgId = profile?.organization_id ?? null;
  const queryClient = useQueryClient();
  const queryKey = ["org-modules", orgId];

  const { data, isLoading, refetch } = useQuery({
    queryKey,
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_org_modules", { p_org: orgId });
      if (error) throw error;
      return (data || []) as OrgModule[];
    },
  });

  const value = useMemo<OrgModulesValue>(() => {
    const modules = data ?? [];
    const moduleMap: Record<string, boolean> = {};
    modules.forEach((m) => {
      moduleMap[m.module_key] = !!m.is_enabled;
    });

    const isEnabled = (key: ModuleKey) => {
      // Fail open — never lock anyone out on a missing org, a pending fetch,
      // or a module key the platform doesn't know about.
      if (!orgId || isLoading || !data) return true;
      return moduleMap[key] !== false;
    };

    const setModule = async (key: ModuleKey, enabled: boolean) => {
      if (!orgId) return;
      // Optimistic flip
      queryClient.setQueryData(queryKey, (prev: OrgModule[] | undefined) =>
        (prev ?? []).map((m) => (m.module_key === key ? { ...m, is_enabled: enabled } : m))
      );
      const { error } = await supabase.rpc("set_org_module", {
        p_org: orgId,
        p_module: key,
        p_enabled: enabled,
      });
      if (error) {
        queryClient.setQueryData(queryKey, (prev: OrgModule[] | undefined) =>
          (prev ?? []).map((m) => (m.module_key === key ? { ...m, is_enabled: !enabled } : m))
        );
        throw error;
      }
    };

    return { isEnabled, modules, moduleMap, isLoading, orgId, setModule, refetch };
  }, [data, isLoading, orgId, queryClient, refetch]);

  return <OrgModulesContext.Provider value={value}>{children}</OrgModulesContext.Provider>;
};

export function useOrgModules() {
  return useContext(OrgModulesContext);
}
